import { Box, Flex, SimpleGrid, Skeleton, Text, useDisclosure } from "@chakra-ui/react";
import React, { useContext, useEffect, useState } from "react";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import { FaFileAlt } from "react-icons/fa";
import ProcessCardConfigureModal from "components/layouts/MainWindow/Navbar/ProcessCardConfigureModal";

// Color palettes per process name
const PROCESS_COLORS: Record<string, { from: string; to: string; badge: string }> = {
  domestic:  { from: "#667eea", to: "#764ba2", badge: "#a78bfa" },
  zuventus:  { from: "#f093fb", to: "#f5576c", badge: "#fb7185" },
  crm:       { from: "#4facfe", to: "#00f2fe", badge: "#38bdf8" },
  foreign:   { from: "#43e97b", to: "#38f9d7", badge: "#34d399" },
  emerging:  { from: "#fa709a", to: "#fee140", badge: "#fbbf24" },
  pr:        { from: "#a18cd1", to: "#fbc2eb", badge: "#c084fc" },
  sr:        { from: "#fda085", to: "#f6d365", badge: "#fb923c" },
};

const DEFAULT_COLOR = { from: "#6b7280", to: "#9ca3af", badge: "#d1d5db" };

interface ProcessWisePendingCountProps {
  onTotalCalculated?: (total: number) => void;
}

export default function ProcessWisePendingCount({ onTotalCalculated }: ProcessWisePendingCountProps): JSX.Element {
  const { activeWorkflow, setActivePage, accountDetails, workflowProcess, toastNotify } = useContext(AppContext);

  // Initialize from sessionStorage if available
  const [counts, setCounts] = useState<Record<string, number>>(() => {
    const saved = sessionStorage.getItem("dashboardProcessCounts");
    return saved ? JSON.parse(saved) : {};
  });

  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [clickedProcess, setClickedProcess] = useState<string>("");

  const isInitialLoad = React.useRef(!sessionStorage.getItem("dashboardProcessCounts"));

  useEffect(() => {
    if(accountDetails && workflowProcess?.processType && workflowProcess?.processName){
    fetchCounts();
    }
  }, [workflowProcess]);

  const fetchCounts = async () => {
    try {
      setLoading(true);
      const payload: any = {};

      const pType = workflowProcess?.processType || sessionStorage.getItem("processType");
      if (pType && pType !== "undefined") {
         payload.processType = pType;
      }

      // If it's not the initial load, we must send the specific processName
      if (!isInitialLoad.current) {
         const pName = workflowProcess?.processName || sessionStorage.getItem("processName");
         if (pName && pName !== "undefined" && pName !== "null") {
             payload.processName = pName;
         } else {
             // We already fetched everything initially, and no specific process is active.
             // Skip duplicate fetching to save network calls.
             if (Object.keys(counts).length > 0) {
                 setLoading(false);
                 return;
             }
         }
      }

      const res = (await appRequest("document", "getAllPendingCount", payload)) as any;
      const newCounts = res?.data || res?.count;
      if (newCounts) {
        setCounts(prev => {
           const updated = { ...prev, ...newCounts };
           sessionStorage.setItem("dashboardProcessCounts", JSON.stringify(updated));
           return updated;
        });
        isInitialLoad.current = false;
      }
    } catch (error) {
      console.error("Failed to fetch process wise pending count:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = async (e: React.MouseEvent, processName: string) => {
    e.stopPropagation();
    try {
      const processType = workflowProcess?.processType || "NonPo";
      const response: any = await appRequest("workflow", "setWorkflowProcess", {
        processName: processName,
        processType: processType,
      });

      if (response?.status === "success") {
        sessionStorage.setItem("processName", processName);
        sessionStorage.setItem("processType", processType);

        toastNotify({
          title: "Success",
          message: response?.data || "Workspace switched successfully!",
          type: "success",
        });

        setTimeout(() => {
          sessionStorage.setItem("activePage", "Approvals");
          window.location.reload();
        }, 800);
      } else {
        toastNotify({
          title: "Error",
          message: response?.data,
          type: "info",
        });
      }
    } catch (err: any) {
      toastNotify({
        title: "Error",
        message: err?.message,
        type: "error",
      });
    }
  };

  const totalPending = Object.values(counts).reduce((a, b) => a + b, 0);
  const currentProcessPending = workflowProcess?.processName ? (counts[workflowProcess.processName] || 0) : 0;
  const entries = Object.entries(counts);

  useEffect(() => {
    if (onTotalCalculated) {
      onTotalCalculated(currentProcessPending);
    }
  }, [currentProcessPending, onTotalCalculated]);

  return (
    <Box mt={4} mb={6}>
      {/* Configure Process Modal — pre-filled with clicked process */}
      <ProcessCardConfigureModal
        isOpen={isOpen}
        onClose={onClose}
        defaultProcessName={clickedProcess}
        defaultProcessType={workflowProcess?.processType || "NonPo"}
      />

      {/* Header row */}
      <Flex align="center" justify="space-between" mb={4}>
        <Box>
          <Text fontSize="lg" fontWeight="800" color="gray.700" letterSpacing="-0.3px">
            Process-Wise Pending
          </Text>
          <Text fontSize="sm" color="gray.400" fontWeight="400">
            Actionable documents across processes
          </Text>
        </Box>
        {!loading && entries.length > 0 && (
          <Box
            bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            color="white"
            px={4}
            py={1.5}
            borderRadius="full"
            fontSize="sm"
            fontWeight="700"
            boxShadow="0 4px 14px rgba(102,126,234,0.45)"
          >
            {totalPending} Total
          </Box>
        )}
      </Flex>

      {loading ? (
        <SimpleGrid columns={[1, 2, 3, 4, 5]} spacing={4}>
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height="110px" borderRadius="2xl" startColor="gray.100" endColor="gray.200" />
          ))}
        </SimpleGrid>
      ) : entries.length > 0 ? (
        <SimpleGrid columns={[1, 2, 3, 4, 5]} spacing={4}>
          {entries.map(([process, count]) => {
            const colors = PROCESS_COLORS[process.toLowerCase()] || DEFAULT_COLOR;
            return (
              <Box
                key={process}
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                bg="white"
                position="relative"
                cursor="pointer"
                onClick={(e) => handleCardClick(e, process)}
                transition="transform 0.2s ease, box-shadow 0.2s ease"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.14)",
                }}
              >
                {/* Gradient top bar */}
                <Box
                  h="5px"
                  bgGradient={`linear(to-r, ${colors.from}, ${colors.to})`}
                />

                <Box p={3}>
                  {/* Icon + badge */}
                  <Flex align="center" justify="space-between" mb={2}>
                    <Box
                      fontSize="1.2rem"
                      lineHeight="1"
                      bg={`${colors.badge}20`}
                      p={1.5}
                      borderRadius="md"
                    >
                      <FaFileAlt size={16} color={colors.from} />
                    </Box>
                    <Box
                      px={2}
                      py={0.5}
                      bg={count > 0 ? "orange.50" : "green.50"}
                      color={count > 0 ? "orange.500" : "green.500"}
                      borderRadius="full"
                      fontSize="10px"
                      fontWeight="700"
                    >
                      {count > 0 ? "Action Needed" : "Clear"}
                    </Box>
                  </Flex>

                  {/* Count */}
                  <Text
                    fontSize="2xl"
                    fontWeight="900"
                    bgGradient={`linear(to-r, ${colors.from}, ${colors.to})`}
                    bgClip="text"
                    lineHeight="1.1"
                    mb={0.5}
                  >
                    {count}
                  </Text>

                  {/* Process name */}
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.600"
                    textTransform="capitalize"
                    letterSpacing="0.3px"
                  >
                     {['pr','sr'].includes(process) ? process.toUpperCase() : process === "crm" ? "Marketing" : process}
                  </Text>
                  <Text fontSize="10px" color="gray.400">
                    pending docs
                  </Text>
                </Box>
              </Box>
            );
          })}
        </SimpleGrid>
      ) : (
        <Flex
          align="center"
          justify="center"
          p={10}
          bg="gray.50"
          borderRadius="2xl"
          border="2px dashed"
          borderColor="gray.200"
        >
          <Text color="gray.400" fontWeight="500">
            No pending documents found.
          </Text>
        </Flex>
      )}
    </Box>
  );
}
